var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { OpenAPIMediaTypeExamplesBody, OpenAPIMediaTypeExamplesSelector, } from './OpenAPICodeSampleInteractive';
import { OpenAPICodeSampleBody } from './OpenAPICodeSampleSelector';
import { ScalarApiButton } from './ScalarApiButton';
import { codeSampleGenerators } from './code-samples';
import { getOpenAPIClientContext } from './context';
import { generateMediaTypeExamples, generateSchemaExample } from './generateSchemaExample';
import { stringifyOpenAPI } from './stringifyOpenAPI';
import { getDefaultServerURL } from './util/server';
import { checkIsReference } from './utils';
var CUSTOM_CODE_SAMPLES_KEYS = ['x-custom-examples', 'x-code-samples', 'x-codeSamples'];
/**
 * Display code samples to execute the operation.
 * It supports the Redocly custom syntax as well (https://redocly.com/docs/api-reference-docs/specification-extensions/x-code-samples/)
 */
export function OpenAPICodeSample(props) {
    var data = props.data, context = props.context;
    // If code samples are disabled at operation level, we don't display the code samples.
    if (data.operation['x-codeSamples'] === false) {
        return null;
    }
    var customCodeSamples = getCustomCodeSamples(props);
    // If code samples are disabled at the top-level and not custom code samples are defined,
    // we don't display the code samples.
    if (data['x-codeSamples'] === false && !customCodeSamples) {
        return null;
    }
    var samples = customCodeSamples !== null && customCodeSamples !== void 0 ? customCodeSamples : generateCodeSamples(props);
    if (samples.length === 0) {
        return null;
    }
    return (<OpenAPICodeSampleBody context={getOpenAPIClientContext(context)} data={data} items={samples} selectIcon={context.icons.chevronDown}/>);
}
/**
 * Generate code samples for the operation.
 */
function generateCodeSamples(props) {
    var _a, _b;
    var data = props.data, context = props.context;
    var searchParams = new URLSearchParams();
    var headersObject = {};
    (_a = data.operation.parameters) === null || _a === void 0 ? void 0 : _a.forEach(function (param) {
        if (!param) {
            return;
        }
        if (param.in === 'header' && param.required) {
            var example = param.schema
                ? generateSchemaExample(param.schema, { mode: 'write' })
                : undefined;
            if (example !== undefined && param.name) {
                headersObject[param.name] =
                    typeof example !== 'string' ? stringifyOpenAPI(example) : example;
            }
        }
        else if (param.in === 'query' && param.required) {
            var example = param.schema
                ? generateSchemaExample(param.schema, { mode: 'write' })
                : undefined;
            if (example !== undefined && param.name) {
                searchParams.append(param.name, String(Array.isArray(example) ? example[0] : example));
            }
        }
    });
    var requestBody = !checkIsReference(data.operation.requestBody)
        ? data.operation.requestBody
        : undefined;
    var url = getDefaultServerURL(data.servers) +
        data.path +
        (searchParams.size ? "?".concat(searchParams.toString()) : '');
    var genericHeaders = __assign(__assign({}, getSecurityHeaders(data.securities)), headersObject);
    var mediaTypeRendererFactories = Object.entries((_b = requestBody === null || requestBody === void 0 ? void 0 : requestBody.content) !== null && _b !== void 0 ? _b : {}).map(function (_a) {
        var mediaType = _a[0], mediaTypeObject = _a[1];
        return function (generator) {
            var mediaTypeHeaders = __assign(__assign({}, genericHeaders), { 'Content-Type': mediaType });
            return {
                mediaType: mediaType,
                element: context.renderCodeBlock({
                    code: generator.generate({
                        url: url,
                        method: data.method,
                        body: undefined,
                        headers: mediaTypeHeaders,
                    }),
                    syntax: generator.syntax,
                }),
                examples: generateMediaTypeExamples(mediaTypeObject, {
                    mode: 'write',
                }).map(function (example) { return ({
                    example: example,
                    element: context.renderCodeBlock({
                        code: generator.generate({
                            url: url,
                            method: data.method,
                            body: example.value,
                            headers: mediaTypeHeaders,
                        }),
                        syntax: generator.syntax,
                    }),
                }); }),
            };
        };
    });
    return codeSampleGenerators.map(function (generator) {
        if (mediaTypeRendererFactories.length > 0) {
            var renderers = mediaTypeRendererFactories.map(function (generate) { return generate(generator); });
            return {
                key: "default-".concat(generator.id),
                label: generator.label,
                body: (<OpenAPIMediaTypeExamplesBody method={data.method} path={data.path} renderers={renderers} blockKey={context.blockKey}/>),
                footer: (<OpenAPICodeSampleFooter renderers={renderers} data={data} context={context}/>),
            };
        }
        return {
            key: "default-".concat(generator.id),
            label: generator.label,
            body: context.renderCodeBlock({
                code: generator.generate({
                    url: url,
                    method: data.method,
                    body: undefined,
                    headers: genericHeaders,
                }),
                syntax: generator.syntax,
            }),
            footer: <OpenAPICodeSampleFooter data={data} renderers={[]} context={context}/>,
        };
    });
}
function OpenAPICodeSampleFooter(props) {
    var data = props.data, context = props.context, renderers = props.renderers;
    var method = data.method, path = data.path;
    var specUrl = context.specUrl;
    var hideTryItPanel = data['x-hideTryItPanel'] || data.operation['x-hideTryItPanel'];
    var hasMultipleMediaTypes = renderers.length > 1 || renderers.some(function (renderer) { return renderer.examples.length > 0; });
    if (hideTryItPanel && !hasMultipleMediaTypes) {
        return null;
    }
    if (!validateHttpMethod(method)) {
        return null;
    }
    return (<div className="openapi-codesample-footer">
            {hasMultipleMediaTypes ? (<OpenAPIMediaTypeExamplesSelector method={data.method} path={data.path} renderers={renderers} selectIcon={context.icons.chevronDown} blockKey={context.blockKey}/>) : (<span />)}
            {!hideTryItPanel && (<ScalarApiButton context={getOpenAPIClientContext(context)} method={method} path={path} specUrl={specUrl}/>)}
        </div>);
}
/**
 * Get custom code samples for the operation.
 */
function getCustomCodeSamples(props) {
    var data = props.data, context = props.context;
    var customCodeSamples = null;
    CUSTOM_CODE_SAMPLES_KEYS.forEach(function (key) {
        var customSamples = data.operation[key];
        if (customSamples && Array.isArray(customSamples)) {
            customCodeSamples = customSamples
                .filter(function (sample) {
                return (typeof sample.label === 'string' &&
                    typeof sample.source === 'string' &&
                    typeof sample.lang === 'string');
            })
                .map(function (sample, index) { return ({
                key: "custom-sample-".concat(sample.lang, "-").concat(index),
                label: sample.label,
                body: context.renderCodeBlock({
                    code: sample.source,
                    syntax: sample.lang,
                }),
                footer: (<OpenAPICodeSampleFooter renderers={[]} data={data} context={context}/>),
            }); });
        }
    });
    return customCodeSamples;
}
function getSecurityHeaders(securities) {
    var _a;
    var _b, _c;
    var security = securities[0];
    if (!security) {
        return {};
    }
    switch (security[1].type) {
        case 'http': {
            var scheme = security[1].scheme;
            var format = (_b = security[1].bearerFormat) !== null && _b !== void 0 ? _b : 'YOUR_SECRET_TOKEN';
            if (scheme === null || scheme === void 0 ? void 0 : scheme.includes('bearer')) {
                scheme = 'Bearer';
            }
            else if (scheme === null || scheme === void 0 ? void 0 : scheme.includes('basic')) {
                scheme = 'Basic';
                format = 'username:password';
            }
            else if (scheme === null || scheme === void 0 ? void 0 : scheme.includes('token')) {
                scheme = 'Token';
            }
            return {
                Authorization: "".concat(scheme, " ").concat(format),
            };
        }
        case 'apiKey': {
            if (security[1].in !== 'header')
                return {};
            var name_1 = (_c = security[1].name) !== null && _c !== void 0 ? _c : 'Authorization';
            return _a = {},
                _a[name_1] = 'YOUR_API_KEY',
                _a;
        }
        default: {
            return {};
        }
    }
}
function validateHttpMethod(method) {
    return ['get', 'post', 'put', 'delete', 'patch', 'head', 'options', 'trace'].includes(method);
}
